import { useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/Button";
import { Icon } from "@/components/Icon";
import {
    matchesNavigation,
    type NavigationArea,
    type NavigationItem,
} from "@/components/workspace/navigation";
import { focusableElements } from "@/components/overlay";

interface FavoriteNavigationProps {
    areas: NavigationArea[];
    favorites: string[];
    onFavorite: (id: string) => void;
    onReorderFavorite: (sourceId: string, targetId: string) => void;
    onNavigate: () => void;
}

export const FavoriteNavigation = ({
    areas,
    favorites,
    onFavorite,
    onReorderFavorite,
    onNavigate,
}: FavoriteNavigationProps) => {
    const { t } = useTranslation("global");
    const { pathname } = useLocation();
    const container = useRef<HTMLDivElement>(null);
    const dragImage = useRef<HTMLDivElement | null>(null);
    const [draggedId, setDraggedId] = useState<string | null>(null);
    const items = areas.flatMap((area) => area.items);
    const visible = favorites
        .map((id) => items.find((item) => item.id === id))
        .filter((item): item is NavigationItem => Boolean(item));
    const remove = (id: string): void => {
        onFavorite(id);
        requestAnimationFrame(() => {
            if (container.current) {
                (
                    focusableElements(container.current)[0] || container.current
                ).focus();
            }
        });
    };
    return (
        <div ref={container} tabIndex={-1} className="space-y-1">
            <p className="px-3 py-2 text-xs font-semibold text-slate-500 dark:text-zinc-400">
                {t("menu.favorites.header")}
            </p>
            {visible.map((item) => (
                <div
                    key={item.id}
                    draggable
                    onDragStart={(event) => {
                        setDraggedId(item.id);
                        event.dataTransfer.effectAllowed = "move";
                        event.dataTransfer.setData("text/plain", item.id);

                        const row = event.currentTarget.cloneNode(
                            true
                        ) as HTMLDivElement;
                        row.removeAttribute("draggable");
                        row.style.position = "fixed";
                        row.style.top = "-10000px";
                        row.style.left = "-10000px";
                        row.style.width = `${event.currentTarget.getBoundingClientRect().width}px`;
                        row.style.background = "white";
                        row.style.border = "1px solid rgb(49 98 141 / 0.35)";
                        row.style.borderRadius = "8px";
                        row.style.boxShadow = "0 10px 24px rgb(15 23 42 / 0.16)";
                        row.style.opacity = "0.96";
                        row.style.pointerEvents = "none";
                        document.body.appendChild(row);
                        dragImage.current = row;
                        event.dataTransfer.setDragImage(row, 24, 20);
                    }}
                    onDragEnd={() => {
                        setDraggedId(null);
                        dragImage.current?.remove();
                        dragImage.current = null;
                    }}
                    onDragOver={(event) => {
                        event.preventDefault();
                        event.dataTransfer.dropEffect = "move";
                    }}
                    onDrop={() => {
                        if (draggedId) {
                            onReorderFavorite(draggedId, item.id);
                        }
                        setDraggedId(null);
                    }}
                    className={`flex cursor-grab items-center gap-0.5 rounded-md transition-colors active:cursor-grabbing hover:bg-slate-100 dark:hover:bg-white/5 ${draggedId === item.id ? "bg-primary/10 opacity-50" : ""}`}
                >
                    <Link
                        to={item.path || "/"}
                        onClick={onNavigate}
                        aria-current={
                            matchesNavigation(item, pathname)
                                ? "page"
                                : undefined
                        }
                        className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-3 py-2.5 text-sm !text-inherit"
                    >
                        <Icon
                            value="heart"
                            size={14}
                            className="fill-primary text-primary shrink-0"
                        />
                        <Icon
                            value={item.icon}
                            size={16}
                            className="shrink-0"
                        />
                        <div className="truncate">{item.label}</div>
                    </Link>
                    <Button
                        variant="ghost"
                        size="xs"
                        onlyicon
                        icon={<Icon value="heart" fill="currentColor" />}
                        className="!h-8 !w-8 !p-0"
                        aria-label={`${t("menu.favorites.tooltip_remove")}: ${item.label}`}
                        onClick={() => remove(item.id)}
                    />
                </div>
            ))}
            {!visible.length && (
                <p className="px-3 py-2 text-sm leading-relaxed text-slate-600 dark:text-zinc-300">
                    {t("workspace.favorites_hint")}
                </p>
            )}
        </div>
    );
};
